import { Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../component-data-source.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, catchError, tap } from 'rxjs/operators';
import { LoadDataParams } from '../../../interfaces/DataSource/DataSourceParams.interface';
import { ExternalUserDTO } from '../../../dto/users/external-user.dto';
import { environment } from '../../../environment/environment';

export function external_users(
  componentDataSourceService: ComponentDataSourceService,
  http: HttpClient,
  router: Router
) {
  return {
    name: 'External Users',
    description: 'List of external users who have participated in organization events',
    execute: (params?: LoadDataParams) => {
      
      // Check if this is a recursive call
      if (params?.queryParams?.['isRecursiveCall']) {
        return of({ items: [], totalCount: 0 });
      }
      
      // Build query parameters from the LoadDataParams
      let queryParams: any = {};
      if (params?.queryParams) {
        queryParams = { ...params.queryParams };
      }
      
      // Set default pagination if not provided
      if (!queryParams.pageSize) {
        queryParams.pageSize = 50; // Reasonable default for external users
      }
      if (!queryParams.page) {
        queryParams.page = 1;
      }
      
      
      // Call the backend API endpoint
      return http.get<any>(`${environment.apiUrl}/users/external`, { params: queryParams }).pipe(
        tap(result => {
        }),
        map(apiResponse => {
          if (!apiResponse?.data) {
            console.error('[external_users] Failed to retrieve external users from API');
            return { items: [], totalCount: 0 };
          }
          
          // Transform the API response data for frontend consumption
          const transformedUsers = apiResponse.data.map((user: ExternalUserDTO) => ({
            uuid: user.uuid,
            name: user.full_name || `${user.first_name} ${user.last_name}`.trim(),
            first_name: user.first_name,
            last_name: user.last_name,
            username: user.username,
            email: user.username, // Assuming username is email
            event_count: user.event_count || 0,
            last_event_date: user.last_event_date,
            event_locations: user.event_locations || '',
            is_external: true,
            type: 'External User', // Type for display purposes
            status: `${user.event_count || 0} events` // Status showing event participation
          }));
          
          // Sort by name for consistent display
          const sortedUsers = transformedUsers.sort((a: any, b: any) => 
            a.name.localeCompare(b.name, 'da', { sensitivity: 'base' })
          );
          
          
          return {
            items: sortedUsers,
            totalCount: apiResponse.meta?.totalItems || sortedUsers.length,
            pageSize: apiResponse.meta?.pageSize || queryParams.pageSize,
            pageIndex: (apiResponse.meta?.page || 1) - 1, // Convert to 0-based index
            meta: apiResponse.meta || {}
          };
        }),
        catchError(error => {
          console.error('[external_users] Error fetching external users:', error);
          return of({ 
            items: [], 
            totalCount: 0,
            error: 'Failed to load external users'
          });
        })
      );
    },
    settings: {
      columns: [
        { key: 'name', header: 'Navn', type: 'text', sortable: true },
        { key: 'username', header: 'Email', type: 'text', sortable: true },
        { 
          key: 'event_count', 
          header: 'Antal Events', 
          type: 'text', 
          sortable: true,
          alignment: 'center',
          transform: (value: number) => value?.toString() || '0'
        },
        { 
          key: 'last_event_date', 
          header: 'Sidste Event', 
          type: 'text', 
          sortable: true,
          transform: (value: string) => {
            if (!value) return 'Aldrig';
            const date = new Date(value);
            return date.toLocaleDateString('da-DK');
          }
        },
        { 
          key: 'event_locations', 
          header: 'Lokationer', 
          type: 'text',
          transform: (value: string) => value || 'Ukendt'
        },
        {
          key: 'actions',
          header: '',
          type: 'button',
          buttonText: 'Profil',
          buttonColor: 'primary',
          alignment: 'right',
          variant: 'raised',
          action: (row: any) => {
            router.navigate(['/profile/' + row.uuid]);
          }
        }
      ],
      pageable: true,
      sortable: true,
      rowHover: true,
      searchable: true,
      defaultSortColumn: 'name',
      defaultSortDirection: 'asc'
    }
  };
} 